import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import apiService from '@/services/api';
import { Pencil, Trash2, Plus } from 'lucide-react';

const Container = styled.div`
  padding: 24px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const Button = styled.button`
  padding: 10px 12px;
  border-radius: 6px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ButtonSmall = styled(Button)`
  padding: 8px 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
  th, td { border-bottom: 1px solid #e5e7eb; padding: 10px; text-align: left; }
  th { background: #f9fafb; font-weight: 600; font-size: 14px; }
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const EmailConfig: React.FC = () => {
  const [form, setForm] = useState({
    description: '',
    host: '',
    port: '' as any,
    username: '',
    password: '',
    from_address: '',
    from_name: '',
    reply_to: '',
    require_tls: false,
  });
  const [list, setList] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    // Carrega apenas a lista; não preenche o formulário automaticamente
    (async () => {
      try {
        const items = await apiService.listarEmailConfigs();
        setList(items);
      } catch {}
    })();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSalvar = async () => {
    try {
      if (editingId) {
        await apiService.atualizarEmailConfig(editingId, form);
      } else {
        await apiService.salvarEmailConfig(form);
      }
      toast.success('Configuração salva');
      const items = await apiService.listarEmailConfigs();
      setList(items);
      setEditingId(null);
      setForm({ ...form, password: '' });
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao salvar');
    }
  };

  const handleNovo = () => {
    setEditingId(null);
    setForm({
      description: '',
      host: '',
      port: '' as any,
      username: '',
      password: '',
      from_address: '',
      from_name: '',
      reply_to: '',
      require_tls: false,
    });
  };

  const handleEditar = async (id: number) => {
    const cfg = await apiService.obterEmailConfigPorId(id);
    if (!cfg) return;
    setEditingId(id);
    setForm({
      description: cfg.description,
      host: cfg.host,
      port: cfg.port,
      username: cfg.username,
      password: '',
      from_address: cfg.from_address,
      from_name: cfg.from_name || '',
      reply_to: cfg.reply_to || '',
      require_tls: Boolean(cfg.require_tls),
    });
  };

  const handleExcluir = async (id: number) => {
    if (!confirm('Confirma excluir esta configuração?')) return;
    await apiService.excluirEmailConfig(id);
    toast.success('Configuração excluída');
    const items = await apiService.listarEmailConfigs();
    setList(items);
  };

  return (
    <Container>
      <Card>
        <Title>Configuração de e-mail</Title>
        <Actions>
          <Button onClick={handleNovo}><Plus size={16} /> Novo</Button>
        </Actions>
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Descrição</th>
              <th>Host</th>
              <th>Usuário</th>
              <th>From</th>
              <th>Atualizado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.description}</td>
                <td>{item.host}:{item.port}</td>
                <td>{item.username}</td>
                <td>{item.from_address}</td>
                <td>{item.updated_at ? new Date(item.updated_at).toLocaleString('pt-BR') : '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <ButtonSmall onClick={() => handleEditar(item.id)}><Pencil size={16} /> Editar</ButtonSmall>
                    <ButtonSmall onClick={() => handleExcluir(item.id)}><Trash2 size={16} /> Excluir</ButtonSmall>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Grid>
          <Field>
            <Label>Descrição</Label>
            <Input name="description" value={form.description} onChange={handleChange} />
          </Field>
          <Field>
            <Label>Host</Label>
            <Input name="host" value={form.host} onChange={handleChange} />
          </Field>
          <Field>
            <Label>Porta</Label>
            <Input name="port" type="number" value={form.port} onChange={handleChange} />
          </Field>
          <Field>
            <Label>Usuário</Label>
            <Input name="username" value={form.username} onChange={handleChange} autoComplete="off" autoCapitalize="off" spellCheck={false} />
          </Field>
          <Field>
            <Label>Senha</Label>
            <Input name="password" type="text" value={form.password} onChange={handleChange} placeholder="(deixe vazio para manter)" autoComplete="new-password" autoCapitalize="off" spellCheck={false} />
          </Field>
          <Field>
            <Label>From</Label>
            <Input name="from_address" value={form.from_address} onChange={handleChange} placeholder="no-reply@seu-dominio.com" />
          </Field>
          <Field>
            <Label>Nome do Remetente</Label>
            <Input name="from_name" value={form.from_name} onChange={handleChange} />
          </Field>
          <Field>
            <Label>Reply-To</Label>
            <Input name="reply_to" value={form.reply_to} onChange={handleChange} />
          </Field>
          <CheckboxRow>
            <input name="require_tls" type="checkbox" checked={form.require_tls} onChange={handleChange} />
            Requer TLS (587)
          </CheckboxRow>
        </Grid>
        <Actions>
          <Button onClick={handleSalvar}>Salvar</Button>
        </Actions>
      </Card>
    </Container>
  );
};

export default EmailConfig;


